import { useTranslation } from "react-i18next";
import { ToggleGroup } from "@/components/ui";
import { CountTabLabel, type CountTabTone } from "@/components/common";
import type { TicketStats as ServiceTicketStats } from "@/services/supportTicketService";
import { TicketStatus, ticketStatusLabel } from "./config";
import {
  isInProgressStatusFilter,
  type TicketListView,
} from "./ticketListFilters";

export type TicketStats = Omit<
  ServiceTicketStats,
  "by_priority" | "by_category"
>;

interface TicketStatsGridProps {
  stats: TicketStats | undefined;
  selectedStatus: TicketStatus | null;
  selectedView: TicketListView | null;
  onStatusSelect: (status: TicketStatus | null) => void;
  onViewSelect: (view: TicketListView) => void;
}

type StripValue = TicketListView | TicketStatus;

const STATUS_TONES: Record<
  | typeof TicketStatus.OPEN
  | typeof TicketStatus.IN_PROGRESS
  | typeof TicketStatus.RESOLVED
  | typeof TicketStatus.CLOSED,
  CountTabTone
> = {
  [TicketStatus.OPEN]: "primary",
  [TicketStatus.IN_PROGRESS]: "accent",
  [TicketStatus.RESOLVED]: "success",
  [TicketStatus.CLOSED]: "neutral",
};

const STATUS_LIST = [
  TicketStatus.OPEN,
  TicketStatus.IN_PROGRESS,
  TicketStatus.RESOLVED,
  TicketStatus.CLOSED,
] as const;

/**
 * Status strip for ticket lists: "active" (default), each status, then "all",
 * with live counts. Doubles as the status filter (no separate status menu).
 */
export function TicketStatsGrid({
  stats,
  selectedStatus,
  selectedView,
  onStatusSelect,
  onViewSelect,
}: TicketStatsGridProps) {
  const { t } = useTranslation("support");
  const inProgressCount = stats
    ? stats.in_progress + stats.waiting_response
    : undefined;
  const ongoingCount =
    stats && inProgressCount !== undefined
      ? stats.open + inProgressCount
      : undefined;

  const value: StripValue = isInProgressStatusFilter(selectedStatus)
    ? TicketStatus.IN_PROGRESS
    : (selectedStatus ?? selectedView ?? "ongoing");

  const countFor = (status: (typeof STATUS_LIST)[number]) => {
    if (!stats) return undefined;
    return status === TicketStatus.IN_PROGRESS
      ? inProgressCount
      : stats[status];
  };

  const options = [
    {
      value: "ongoing" as StripValue,
      label: (
        <CountTabLabel
          label={t("list.stats.ongoing")}
          count={ongoingCount}
          tone="warning"
          active={value === "ongoing"}
        />
      ),
    },
    ...STATUS_LIST.map((status) => ({
      value: status as StripValue,
      label: (
        <CountTabLabel
          label={ticketStatusLabel(status)}
          count={countFor(status)}
          tone={STATUS_TONES[status]}
          showDot
          active={value === status}
        />
      ),
    })),
    {
      value: "all" as StripValue,
      label: (
        <CountTabLabel
          label={t("list.stats.total")}
          count={stats?.total}
          active={value === "all"}
        />
      ),
    },
  ];

  return (
    <div className="-mx-1 overflow-x-auto px-1 pb-0.5">
      <ToggleGroup<StripValue>
        variant="segmented-neutral"
        value={value}
        ariaLabel={t("list.stats.filterLabel")}
        onChange={(next) => {
          if (next === "ongoing" || next === "all") {
            onViewSelect(next);
            return;
          }
          onStatusSelect(next);
        }}
        options={options}
      />
    </div>
  );
}
